import { createFileRoute, useSearch } from '@tanstack/react-router'
import { useState, useMemo, useEffect, Suspense, lazy } from 'react'
import { usePostHog } from 'posthog-js/react'
import { Search as SearchIcon } from 'lucide-react'
import { TabNavigation } from '@/components/shared/molecules/TabNavigation'
import { SkeletonTabPeople } from '@/components/search/TabPeople'
import { AnalyticsEvents } from '@/utils/analytics'

const TabPeople = lazy(() => import('@/components/search/TabPeople'))
const TabCollection = lazy(() => import('@/components/search/TabCollection'))
const TabProducts = lazy(() => import('@/components/search/TabProducts'))

const tabs = [
    { value: 'products', label: 'Products' },
    { value: 'people', label: 'People' },
    { value: 'collections', label: 'Collections' },
]

export const Route = createFileRoute('/search')({
    component: SearchPage,
    validateSearch: (search: Record<string, unknown>) => {
        return {
            q: (search.q as string) || '',
        }
    },
})

function SearchPage() {
    const [activeTab, setActiveTab] = useState('products')
    const search = useSearch({ from: '/search' })
    const [searchQuery, setSearchQuery] = useState(search.q || '')
    const [inputValue, setInputValue] = useState(search.q || '')
    const posthog = usePostHog()

    const debouncedSetSearchQuery = useMemo(() => {
        return debounce((value: string) => {
            setSearchQuery(value)
            // Track search query submission
            if (value.trim()) {
                posthog?.capture(AnalyticsEvents.SEARCH_QUERY_SUBMITTED, {
                    query: value,
                    search_type: activeTab,
                })
            }
        }, 500)
    }, [posthog, activeTab])

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setInputValue(value)

        // Track search initiated (first character typed)
        if (value.length === 1 && inputValue.length === 0) {
            posthog?.capture(AnalyticsEvents.SEARCH_INITIATED, {
                search_type: activeTab,
            })
        }

        debouncedSetSearchQuery(value)
    }

    const handleTabChange = (newTab: string) => {
        posthog?.capture(AnalyticsEvents.SEARCH_TAB_CHANGED, {
            from_tab: activeTab,
            to_tab: newTab,
            has_query: !!searchQuery,
        })
        setActiveTab(newTab)
    }

    // Watch for URL parameter changes
    useEffect(() => {
        if (search.q !== searchQuery) {
            setSearchQuery(search.q || '')
            setInputValue(search.q || '')
        }
    }, [search.q])

    useEffect(() => {
        return () => {
            debouncedSetSearchQuery.cancel()
        }
    }, [debouncedSetSearchQuery])

    return (
        <div className="mx-auto flex w-full flex-col md:px-6">
            {/* Mobile Search bar */}
            <div className="mt-6 px-3 md:hidden">
                <h4 className="mb-4 text-xl font-semibold">Search</h4>
                <div className="flex rounded-xl border border-[#f0f0f0] px-4 py-3.5">
                    <SearchIcon color="#858585" />
                    <input
                        placeholder="Search"
                        className="ml-2 w-full border-none text-base text-[#858585] placeholder:text-[#858585] focus:border-none focus:outline-none active:ring-0"
                        value={inputValue}
                        onChange={handleSearch}
                    />
                </div>
                {searchQuery && (
                    <p className="mt-2 text-sm text-gray-500">
                        Searching for: {searchQuery}
                    </p>
                )}
            </div>
            <div className="mt-2 mb-16">
                <TabNavigation
                    tabs={tabs}
                    activeTab={activeTab}
                    setActiveTab={handleTabChange}
                    variant="search"
                />
                <Suspense fallback={<SkeletonTabPeople />}>
                    {activeTab === 'people' && (
                        <TabPeople searchQuery={searchQuery} />
                    )}
                    {activeTab === 'products' && (
                        <TabProducts searchQuery={searchQuery} />
                    )}
                    {activeTab === 'collections' && (
                        <TabCollection searchQuery={searchQuery} />
                    )}
                </Suspense>
            </div>
        </div>
    )
}

// Helper: debounce function (if lodash.debounce is not available)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function debounce<T extends (...args: any[]) => void>(fn: T, delay: number) {
    let timer: ReturnType<typeof setTimeout>
    const debounced = (...args: Parameters<T>) => {
        clearTimeout(timer)
        timer = setTimeout(() => fn(...args), delay)
    }
    debounced.cancel = () => clearTimeout(timer)
    return debounced as T & { cancel: () => void }
}
